import { useTripItemEvents } from '@/api/trips';
import { Skeleton } from '@/components/library/ui/Skeleton';
import type { TripItemEvent } from '@ultimate-checklist/shared';
import { ChevronDown } from 'lucide-react';

/** Human sentence for one event, e.g. "marked done", "added label “beach”". */
export function describeTripItemEvent(event: TripItemEvent): string {
  const data = event.data ?? {};
  const str = (key: string) => (typeof data[key] === 'string' ? (data[key] as string) : '');
  const fields = asRecord(data.fields);
  const fieldAfter = (key: string) => {
    const change = asRecord(fields?.[key]);
    return typeof change?.after === 'string' ? change.after : '';
  };
  switch (event.type) {
    case 'created':
      return 'added this item';
    case 'status_changed': {
      const to = str('to') || fieldAfter('status');
      if (to === 'done') return 'checked it off';
      if (to === 'doing') return 'marked it in progress';
      return 'unchecked it';
    }
    case 'renamed':
      return `renamed it to “${fieldAfter('name') || str('to') || str('name')}”`;
    case 'note_changed':
      return 'edited the details';
    case 'for_person_changed':
      return 'changed who it is for';
    case 'assigned':
      return 'claimed this';
    case 'unassigned':
      return 'unassigned this';
    case 'label_added':
      return `added label “${str('label')}”`;
    case 'label_removed':
      return `removed label “${str('label')}”`;
    case 'question_opened':
      return str('text') ? `asked: “${str('text')}”` : 'opened a question';
    case 'question_resolved':
      return 'resolved the question';
    case 'bought':
      return 'marked it bought';
    case 'comment_added':
      return 'commented';
    case 'comment_edited':
      return 'edited a comment';
    case 'comment_deleted':
      return 'deleted a comment';
    case 'flagged':
      return 'flagged this item';
    case 'unflagged':
      return 'removed the flag';
    case 'lesson_created': {
      const lessonType = str('lessonType');
      if (lessonType === 'forgot') return 'captured a forgot-this lesson';
      if (lessonType === 'didnt_need') return 'captured a didn’t-need-it lesson';
      return 'captured a lesson note';
    }
    case 'item_linked': {
      const controllerName = str('controllerName');
      return controllerName ? `linked this to “${controllerName}”` : 'linked this item';
    }
    case 'item_unlinked':
      return 'unlinked this item';
    case 'deleted':
      return 'removed this item';
    case 'restored':
      return 'restored this item';
    case 'moved': {
      const to = asRecord(data.to);
      const toName = typeof to?.originMasterListName === 'string' ? to.originMasterListName : '';
      return toName ? `moved it to “${toName}”` : 'moved it to Manually added items';
    }
    case 'list_promoted': {
      const count = typeof data.count === 'number' ? (data.count as number) : 0;
      return count
        ? `pushed this list to the Library (${count} item${count === 1 ? '' : 's'})`
        : 'pushed this list to the Library';
    }
    case 'list_renamed':
      return str('to') ? `renamed this list to “${str('to')}”` : 'renamed this list';
    case 'list_removed': {
      const count = typeof data.count === 'number' ? (data.count as number) : 0;
      return count
        ? `removed this list (${count} item${count === 1 ? '' : 's'})`
        : 'removed this list';
    }
    default:
      return 'made a change';
  }
}

/** Compact relative time, e.g. "just now", "3h ago", "2d ago". */
export function tripItemEventTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'empty';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

export function TripItemEventDetails({ event }: { event: TripItemEvent }) {
  const data = event.data ?? {};
  const fields = asRecord(data.fields);
  const comment = asRecord(data.comment);
  const lesson = asRecord(data.lesson);
  const before = asRecord(data.before);
  const after = asRecord(data.after);
  const hasDetails = fields || comment || lesson || before || after || Object.keys(data).length > 0;
  if (!hasDetails) {
    return null;
  }

  return (
    <details className="group mt-2 rounded-md border border-[var(--border)] bg-[var(--background)]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-[var(--muted-foreground)]">
        View details
        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
      </summary>
      <div className="grid gap-3 border-t border-[var(--border)] p-3 text-xs">
        {comment ? (
          <div className="grid gap-1">
            {comment.before !== undefined ? (
              <div>
                <p className="font-medium">Before</p>
                <p className="whitespace-pre-wrap text-[var(--muted-foreground)]">
                  {valueText(comment.before)}
                </p>
              </div>
            ) : null}
            {comment.after !== undefined ? (
              <div>
                <p className="font-medium">After</p>
                <p className="whitespace-pre-wrap text-[var(--muted-foreground)]">
                  {valueText(comment.after)}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
        {lesson ? (
          <div className="grid gap-1">
            <p className="font-medium">Lesson</p>
            <p className="whitespace-pre-wrap text-[var(--muted-foreground)]">
              {valueText(lesson.text)}
            </p>
          </div>
        ) : null}
        {fields ? (
          <div className="grid gap-2">
            {Object.entries(fields).map(([field, change]) => {
              const record = asRecord(change);
              return (
                <div key={field} className="grid gap-1">
                  <p className="font-medium">{field}</p>
                  {record && ('before' in record || 'after' in record) ? (
                    <div className="grid gap-1 text-[var(--muted-foreground)]">
                      <p className="whitespace-pre-wrap">Before: {valueText(record.before)}</p>
                      <p className="whitespace-pre-wrap">After: {valueText(record.after)}</p>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-[var(--muted-foreground)]">
                      {valueText(change)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
        {before || after ? (
          <div className="grid gap-2">
            {before ? (
              <div>
                <p className="font-medium">Before</p>
                <pre className="overflow-auto whitespace-pre-wrap rounded bg-[var(--muted)] p-2 text-[var(--muted-foreground)]">
                  {JSON.stringify(before, null, 2)}
                </pre>
              </div>
            ) : null}
            {after ? (
              <div>
                <p className="font-medium">After</p>
                <pre className="overflow-auto whitespace-pre-wrap rounded bg-[var(--muted)] p-2 text-[var(--muted-foreground)]">
                  {JSON.stringify(after, null, 2)}
                </pre>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export function TripItemActivity({ tripId, itemId }: { tripId: string; itemId: string }) {
  const events = useTripItemEvents(tripId, itemId);

  if (events.isLoading) {
    return (
      <div className="flex flex-col gap-3 p-1">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-3/4" />
      </div>
    );
  }

  if (events.isError) {
    return (
      <p className="p-4 text-sm text-[var(--muted-foreground)]">Couldn’t load the activity.</p>
    );
  }

  const list = events.data ?? [];
  if (list.length === 0) {
    return <p className="p-4 text-sm text-[var(--muted-foreground)]">No activity yet.</p>;
  }

  return (
    <ol className="flex flex-col gap-0 p-1">
      {list.map((event, index) => (
        <li key={event.id} className="flex gap-3">
          {/* timeline rail */}
          <div className="flex w-4 flex-col items-center">
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--primary)]" />
            {index < list.length - 1 ? <span className="w-px flex-1 bg-[var(--border)]" /> : null}
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm">
              <span className="font-medium">{event.actorName ?? 'Someone'}</span>{' '}
              <span className="text-[var(--muted-foreground)]">{describeTripItemEvent(event)}</span>
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              {tripItemEventTimeAgo(event.createdAt)}
            </p>
            <TripItemEventDetails event={event} />
          </div>
        </li>
      ))}
    </ol>
  );
}
